"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, ShieldCheck, ArrowUpRight, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
function RecursiveFooterMenu({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={depth === 0 ? "space-y-2.5" : "pl-3 mt-1.5 space-y-1.5 border-l border-secondary-foreground/15"}>
      {items.map((item, i) => <li key={item.id || i} data-index={i} className="text-sm">
          <Link to={item.href || "#"} {.../^https?:\/\//.test(item.href || "") && !(item.href || "").includes("gn-construction.com") ? {
        target: "_blank",
        rel: "noopener noreferrer"
      } : {}} className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors inline-flex items-center gap-1.5 group py-0.5">
            <ChevronRight className="size-3 text-secondary-foreground/40 group-hover:text-secondary-foreground/90 transition-transform group-hover:translate-x-0.5" />
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterMenu items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2.5">
        <div className="h-4 w-28 bg-secondary-foreground/10 animate-pulse rounded-md" />
        <div className="h-4 w-36 bg-secondary-foreground/10 animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-secondary-foreground/10 animate-pulse rounded-md" />
        <div className="h-4 w-32 bg-secondary-foreground/10 animate-pulse rounded-md" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-2.5">
        {[{
        label: "Home",
        href: "/HomePage"
      }, {
        label: "Services",
        href: "/services/"
      }, {
        label: "Projects",
        href: "/ProjectsPage"
      }, {
        label: "About Us",
        href: "/AboutPage"
      }, {
        label: "Our Process",
        href: "/OurProcessPage"
      }].map((fallbackLink, i) => <li key={fallbackLink.href} data-index={i} className="text-sm">
            <Link to={fallbackLink.href} className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors inline-flex items-center gap-1.5 group py-0.5">
              <ChevronRight className="size-3 text-secondary-foreground/40 group-hover:text-secondary-foreground/90 transition-transform group-hover:translate-x-0.5" />
              <span>{fallbackLink.label}</span>
            </Link>
          </li>)}
      </ul>;
  }
  return <RecursiveFooterMenu items={menuItems} />;
}
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  "@id": "https://gn-construction.com/#localbusiness",
  "name": "Good Neighbor Construction",
  "url": "https://gn-construction.com/",
  "telephone": "+1-909-906-3355",
  "email": "contact@gn-construction.com",
  "logo": "https://gn-construction.com/wp-content/uploads/2026/09/download-1.png",
  "image": "https://gn-construction.com/wp-content/uploads/2026/09/IMG_7539.jpg",
  "description": "Licensed California general contractor (CSLB #1134435) for kitchen remodels, bathroom remodels, ADUs, home additions and whole-home renovations.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Redlands",
    "addressRegion": "CA",
    "addressCountry": "US"
  },
  "areaServed": [{
    "@type": "AdministrativeArea",
    "name": "San Bernardino County, CA"
  }, {
    "@type": "AdministrativeArea",
    "name": "Riverside County, CA"
  }, {
    "@type": "AdministrativeArea",
    "name": "Orange County, CA"
  }, {
    "@type": "AdministrativeArea",
    "name": "Los Angeles County, CA"
  }, "Redlands, CA", "Rancho Cucamonga, CA", "Upland, CA", "Claremont, CA", "Chino Hills, CA", "Eastvale, CA", "Corona, CA", "Riverside, CA", "Temecula, CA", "Murrieta, CA", "Yorba Linda, CA", "Anaheim Hills, CA", "Brea, CA", "Fullerton, CA", "Tustin, CA", "Orange, CA"],
  "openingHoursSpecification": [{
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "08:00",
    "closes": "18:00"
  }, {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": "Saturday",
    "opens": "09:00",
    "closes": "15:00"
  }],
  "hasCredential": {
    "@type": "EducationalOccupationalCredential",
    "credentialCategory": "license",
    "name": "California Contractors State License Board License #1134435 (Class B General Building)",
    "recognizedBy": {
      "@type": "Organization",
      "name": "Contractors State License Board",
      "url": "https://www.cslb.ca.gov/"
    }
  },
  "sameAs": ["https://www.instagram.com/goodneighbor.construction/", "https://www.tiktok.com/@gn_construction", "https://www.yelp.com/biz/good-neighbor-construction-redlands"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Remodeling and construction services",
    "itemListElement": [{
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Kitchen Remodeling",
        "url": "https://gn-construction.com/kitchen-remodeling/"
      }
    }, {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Bathroom Remodeling",
        "url": "https://gn-construction.com/bathroom-remodeling/"
      }
    }, {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "ADU Construction",
        "url": "https://gn-construction.com/adu-construction/"
      }
    }, {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Home Additions",
        "url": "https://gn-construction.com/home-additions/"
      }
    }, {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": "Whole-Home Remodeling",
        "url": "https://gn-construction.com/services/"
      }
    }]
  }
};
export default function Footer() {
  const serviceAreas = ["San Bernardino County", "Riverside County", "Orange County", "Los Angeles County"];
  const primaryServices = [{
    label: "Kitchen Remodeling",
    href: "/kitchen-remodeling/"
  }, {
    label: "Bathroom Remodeling",
    href: "/bathroom-remodeling/"
  }, {
    label: "ADU Construction",
    href: "/adu-construction/"
  }, {
    label: "Home Additions",
    href: "/home-additions/"
  }, {
    label: "Roofing",
    href: "/roofing/"
  }, {
    label: "Painting",
    href: "/painting/"
  }, {
    label: "Whole-Home Remodeling",
    href: "/services/"
  }, {
    label: "Residential Repairs",
    href: "/services/"
  }];
  return <section id="footer" data-nav="dark" className="text-secondary-foreground relative border-t border-border/20 pt-16 pb-12 overflow-hidden light bg-accent-foreground">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(localBusinessSchema)
    }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-14">
          
          {/* Brand & Credentials */}
          <div className="lg:col-span-4 space-y-6">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-12 w-auto object-contain brightness-0 invert" />
            </Link>
            
            <p className="text-sm text-secondary-foreground/80 leading-relaxed max-w-sm">
              Professional residential construction and remodeling across Southern California. 
              Dedicated to craftsmanship, transparent communication, and reliable project delivery.
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-foreground/10 border border-secondary-foreground/15 text-xs text-secondary-foreground/90">
              <ShieldCheck className="size-4 text-chart-3" />
              <span>Licensed, Bonded & Insured (CSLB #1134435)</span>
            </div>

            <div className="pt-2">
              <Link to="/#request-estimate">
                <Button variant="outline" size="sm" className="bg-secondary-foreground/10 border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/20 hover:text-secondary-foreground gap-1.5 text-xs">
                  <span>Start Your Project</span>
                  <ArrowUpRight className="size-3.5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-sm font-semibold tracking-wide uppercase text-secondary-foreground">
              Navigation
            </h4>
            <MenuProvider menu_id="7" className="footer-menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Core Services */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-sm font-semibold tracking-wide uppercase text-secondary-foreground">
              Services
            </h4>
            <ul className="space-y-2.5">
              {primaryServices.map((service, i) => <li key={service.label} data-index={i} className="text-sm">
                  <Link to={service.href} className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors inline-flex items-center gap-1.5 group py-0.5">
                    <ChevronRight className="size-3 text-secondary-foreground/40 group-hover:text-secondary-foreground/90 transition-transform group-hover:translate-x-0.5" />
                    <span>{service.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Contact & Hours */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-sm font-semibold tracking-wide uppercase text-secondary-foreground">
              Contact & Hours
            </h4>
            
            <div className="space-y-3.5 text-sm">
              <div className="flex items-start gap-3 text-secondary-foreground/85">
                <MapPin className="size-4 text-chart-1 shrink-0 mt-0.5" />
                <span>Based in Redlands, CA. Serving the Inland Empire, Orange County &amp; Los Angeles</span>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="size-4 text-chart-1 shrink-0" />
                <a href="tel:+19099063355" className="text-secondary-foreground/85 hover:text-secondary-foreground transition-colors font-medium">(909) 906-3355</a>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="size-4 text-chart-1 shrink-0" />
                <a href="mailto:contact@gn-construction.com" className="text-secondary-foreground/85 hover:text-secondary-foreground transition-colors break-all">contact@gn-construction.com</a>
              </div>

              <div className="flex items-start gap-3 pt-1 text-xs text-secondary-foreground/75 leading-normal">
                <Clock className="size-4 text-secondary-foreground/60 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-secondary-foreground/90">Monday - Friday: 8:00 AM - 6:00 PM</p>
                  <p>Saturday: 9:00 AM - 3:00 PM</p>
                  <p className="text-secondary-foreground/60">Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Service Area Badges Section */}
        <div className="py-6 border-t border-secondary-foreground/15">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground/70">
                Service Communities:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {serviceAreas.map((area, i) => <div key={area} data-index={i}>
                  <Badge variant="outline" className="bg-secondary-foreground/5 border-secondary-foreground/15 text-secondary-foreground/80 hover:bg-secondary-foreground/10 text-xs font-normal">
                    {area}
                  </Badge>
                </div>)}
            </div>
          </div>
        </div>

        <Separator className="bg-secondary-foreground/15 my-6" />

        {/* Bottom Bar: Copyright & Compliance */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-secondary-foreground/70">
          <p>
            {"© "}
            {new Date().getFullYear()}
            {" Good Neighbor Construction. All rights reserved."}
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy-policy/" className="hover:text-secondary-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions/" className="hover:text-secondary-foreground transition-colors">Terms and Conditions</Link>
            <Link to="https://www.cslb.ca.gov/OnlineServices/CheckLicenseII/LicenseDetail.aspx?LicNum=1134435" newTab className="hover:text-secondary-foreground transition-colors">
              CSLB License #1134435
            </Link>
            <Link to="https://www.google.com/maps/search/Good+Neighbor+Construction+Redlands+CA" newTab className="hover:text-secondary-foreground transition-colors">Google</Link>
            <Link to="https://www.yelp.com/biz/good-neighbor-construction-redlands" newTab className="hover:text-secondary-foreground transition-colors">Yelp</Link>
            <Link to="https://www.instagram.com/goodneighbor.construction/" newTab className="hover:text-secondary-foreground transition-colors">Instagram</Link>
            <Link to="https://www.tiktok.com/@gn_construction" newTab className="hover:text-secondary-foreground transition-colors">TikTok</Link>
          </div>
        </div>

      </div>
    </section>;
}